import { useCallback, useRef, useEffect } from "react";
import { cn } from "~/widgets/lib/utils";
import { pushToGoogleAds } from "~/widgets/lib/tracking";
import type { GiftcardBuyNowButtonProps } from "./types";
import { GiftcardCheckoutOverlay } from "../GiftcardCheckoutOverlay/GiftcardCheckoutOverlay";

// Button style variants
const buttonStyleClasses = {
  rounded: "rounded-lg",
  square: "rounded-none",
  pill: "rounded-full",
} as const;

export function GiftcardBuyNowButton({
  businessId,
  businessSlug,
  apiUrl,
  mode = "popup",
  buttonText = "Buy Gift Card",
  buttonClassName,
  buttonStyle = "pill",
  theme = "light",
  primaryColor = "#03c2b9",
  widgetBaseUrl,
  gtagConversionId,
  gtagConversionLabel,
}: GiftcardBuyNowButtonProps) {
  const overlayRef = useRef<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    // Remove the checkout overlay from the DOM
    if (overlayRef.current && overlayRef.current.parentNode) {
      overlayRef.current.parentNode.removeChild(overlayRef.current);
      overlayRef.current = null;
    }
  }, []);

  const handleComplete = useCallback(() => {
    const event = new CustomEvent("baluu:giftcard-purchased", {
      bubbles: true,
      composed: true,
      detail: {
        businessId,
        businessSlug,
      },
    });
    document.dispatchEvent(event);

    setTimeout(() => {
      handleClose();
    }, 3000);
  }, [businessId, businessSlug, handleClose]);

  const handleOpen = useCallback(() => {
    // Create the checkout overlay web component
    const overlay = document.createElement("baluu-giftcard-checkout-overlay");

    // Set attributes
    overlay.setAttribute("business-id", businessId);
    if (businessSlug) overlay.setAttribute("business-slug", businessSlug);
    if (apiUrl) overlay.setAttribute("api-url", apiUrl);
    if (theme) overlay.setAttribute("theme", theme);
    if (primaryColor) overlay.setAttribute("primary-color", primaryColor);
    if (widgetBaseUrl) overlay.setAttribute("widget-base-url", widgetBaseUrl);
    overlay.setAttribute("mode", "popup");

    // Store reference
    overlayRef.current = overlay;

    // Append to body (outside any Shadow DOM)
    document.body.appendChild(overlay);

    // Listen for close event from the overlay
    const handleOverlayClose = () => {
      handleClose();
    };

    const handleOverlayComplete = (e: Event) => {
      handleComplete();

      if (gtagConversionId && gtagConversionLabel) {
        const detail = (e as CustomEvent).detail || {};
        pushToGoogleAds(gtagConversionId, gtagConversionLabel, {
          businessId,
          purchaseId: detail.purchaseId,
          total: detail.total,
          currency: detail.currency,
        });
      }
    };

    overlay.addEventListener("baluu:giftcard-checkout-close", handleOverlayClose);
    overlay.addEventListener("baluu:giftcard-purchased", handleOverlayComplete);
  }, [
    businessId,
    businessSlug,
    apiUrl,
    theme,
    primaryColor,
    widgetBaseUrl,
    gtagConversionId,
    gtagConversionLabel,
    handleClose,
    handleComplete,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (overlayRef.current && overlayRef.current.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current);
      }
    };
  }, []);

  // For inline mode, render the checkout directly
  if (mode === "inline") {
    return (
      <div className="baluu-widget-root w-full">
        <GiftcardCheckoutOverlay
          businessId={businessId}
          businessSlug={businessSlug}
          apiUrl={apiUrl}
          theme={theme}
          primaryColor={primaryColor}
          widgetBaseUrl={widgetBaseUrl}
          mode="inline"
        />
      </div>
    );
  }

  // Get border radius class based on button style
  const borderRadiusClass =
    buttonStyleClasses[buttonStyle] || buttonStyleClasses.pill;

  // For popup mode, render the button
  return (
    <div className="baluu-widget-root">
      <button
        onClick={handleOpen}
        className={cn(
          // Stable hook class for dashboard "Custom CSS" overrides - do not rename
          "baluu-giftcard-button",
          "inline-flex items-center justify-center",
          "px-6 py-3 font-medium text-base",
          "bg-primary text-primary-foreground",
          "transition-all cursor-pointer",
          "hover:opacity-90 hover:shadow-lg",
          "active:scale-95",
          borderRadiusClass,
          buttonClassName,
        )}
      >
        {buttonText}
      </button>
    </div>
  );
}
